'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { clsx } from 'clsx';
import { Cookie, ShieldCheck, BarChart3, ChevronDown } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Link } from '@/navigation';
import { usePastHero } from '@/lib/usePastHero';
import { hasDecision, readConsent, writeConsent } from '@/lib/consent';

// The footer's "Manage cookies" button dispatches this window event so a visitor
// can re-open the banner and change their choice after having decided once.
export const OPEN_COOKIE_BANNER_EVENT = 'cookie:open-settings';

// Polish RODO/GDPR: non-essential cookies require opt-in. The decision lives in
// the `cookie-consent` cookie (see src/lib/consent.ts) — the single source of
// truth shared with the analytics gate. The banner is collapsed by default;
// "Settings" reveals the per-category toggles. Essential is always on; the only
// optional category is Analytical (Google Analytics), which loads only after
// acceptance — rejecting/withdrawing clears its cookies and kills gtag.
export default function CookieBanner() {
  const t = useTranslations('cookieBanner');
  const reduceMotion = useReducedMotion();
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // Analytics toggle — pre-ticked ON by default (per request). NOTE: a
  // pre-selected analytics box is not GDPR/RODO-valid consent. GA still only
  // loads once a decision is recorded (Accept all / Save choice). On reopen the
  // toggle reflects the visitor's actual saved choice, not this default.
  const [analytics, setAnalytics] = useState(true);
  // Only surface the banner once the user is past the hero (it's a bottom bar,
  // so it would otherwise sit over the hero frames), matching the dock.
  const pastHero = usePastHero();

  // First visit with no decision → show the banner. Otherwise mirror the saved
  // choice so a later reopen shows the toggle in its current state.
  useEffect(() => {
    if (!hasDecision()) setVisible(true);
    else setAnalytics(readConsent() === 'accepted');
  }, []);

  // Footer "Manage cookies" → reopen. Always start COLLAPSED (settings hidden);
  // pre-set the toggle to the saved choice for when the user expands Settings.
  useEffect(() => {
    const reopen = () => {
      setAnalytics(readConsent() === 'accepted');
      setExpanded(false);
      setVisible(true);
    };
    window.addEventListener(OPEN_COOKIE_BANNER_EVENT, reopen);
    return () => window.removeEventListener(OPEN_COOKIE_BANNER_EVENT, reopen);
  }, []);

  // Persist + broadcast (writeConsent clears _ga* on reject and notifies the gate).
  function decide(choice: 'accepted' | 'rejected') {
    writeConsent(choice);
    setAnalytics(choice === 'accepted');
    setExpanded(false);
    setVisible(false);
  }

  // The leftmost button: reveals Settings when collapsed, saves the toggle choice
  // when already expanded.
  function onSettingsOrSave() {
    if (!expanded) setExpanded(true);
    else decide(analytics ? 'accepted' : 'rejected');
  }

  return (
    <AnimatePresence>
      {visible && pastHero && (
        <motion.div
          role="dialog"
          aria-label={t('title')}
          initial={reduceMotion ? false : { y: '110%' }}
          animate={{ y: 0 }}
          exit={reduceMotion ? { opacity: 0 } : { y: '110%' }}
          transition={{ type: 'spring', stiffness: 260, damping: 30 }}
          className="fixed inset-x-0 bottom-0 z-[90] border-t border-outline-variant/20 bg-surface/95 px-margin-mobile pt-3 pb-[calc(0.75rem_+_env(safe-area-inset-bottom))] backdrop-blur-xl lg:px-margin-desktop lg:pt-5 lg:pb-[calc(1.25rem_+_env(safe-area-inset-bottom))]"
        >
          <div className="mx-auto w-full max-w-6xl">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between lg:gap-10">
              {/* Copy */}
              <div className="flex min-w-0 items-start gap-2.5">
                <Cookie size={18} className="mt-0.5 shrink-0 text-on-surface-variant" aria-hidden />
                <div className="min-w-0">
                  <p className="font-headline text-[14px] font-bold leading-snug text-on-surface">
                    {t('title')}
                  </p>
                  <p className="mt-0.5 max-w-3xl text-[12px] leading-relaxed text-on-surface-variant">
                    {t('message')}{' '}
                    <Link
                      href="/cookies"
                      className="text-on-surface underline transition-colors hover:text-primary"
                    >
                      {t('policyLink')}
                    </Link>
                  </p>
                </div>
              </div>

              {/* Actions — one row of chips on mobile (equal thirds so they never
                  wrap to a second line in any locale); auto-width on desktop. */}
              <div className="flex w-full shrink-0 items-stretch gap-1.5 sm:gap-2 lg:w-auto lg:justify-end">
                <Chip variant="secondary" onClick={onSettingsOrSave}>
                  {expanded ? t('save') : t('settings')}
                  <ChevronDown
                    size={14}
                    className={clsx('shrink-0 transition-transform', expanded && 'rotate-180')}
                    aria-hidden
                  />
                </Chip>
                <Chip variant="secondary" onClick={() => decide('rejected')}>
                  {t('essentialOnly')}
                </Chip>
                <Chip variant="primary" className="cta-gold-hover" onClick={() => decide('accepted')}>
                  {t('acceptAll')}
                </Chip>
              </div>
            </div>

            {/* Collapsible per-category settings */}
            <AnimatePresence initial={false}>
              {expanded && (
                <motion.div
                  initial={reduceMotion ? false : { height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={reduceMotion ? { opacity: 0 } : { height: 0, opacity: 0 }}
                  transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden"
                >
                  <div className="mt-3 divide-y divide-outline-variant/20 rounded-xl border border-outline-variant/40 bg-surface-container/50">
                    <ConsentRow
                      icon={ShieldCheck}
                      label={t('essentialLabel')}
                      desc={t('essentialDesc')}
                      checked
                      disabled
                    />
                    <ConsentRow
                      icon={BarChart3}
                      label={t('analyticalLabel')}
                      desc={t('analyticalDesc')}
                      checked={analytics}
                      onChange={setAnalytics}
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Compact pill button. On mobile the three chips share one row as equal thirds
// (flex-1) so they never wrap to a second row in any locale; on sm+ they size to
// their content. Mirrors the site's primary/secondary button styling.
function Chip({
  variant,
  onClick,
  className,
  children,
}: {
  variant: 'primary' | 'secondary';
  onClick: () => void;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'flex flex-1 items-center justify-center gap-1 rounded-full border text-center font-headline font-bold leading-tight transition-colors sm:flex-none',
        'px-2 py-2 text-[11px] sm:px-5 sm:py-2.5 sm:text-label-caps',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
        variant === 'primary'
          ? 'border-on-surface bg-on-surface text-background hover:opacity-90'
          : 'border-on-surface bg-transparent text-on-surface hover:bg-on-surface/10',
        className
      )}
    >
      {children}
    </button>
  );
}

// One consent category: icon + label + short description on the left, a switch on
// the right. The essential category passes `disabled` so it reads as locked-on.
function ConsentRow({
  icon: Icon,
  label,
  desc,
  checked,
  disabled,
  onChange,
}: {
  icon: LucideIcon;
  label: string;
  desc: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (next: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 p-3 sm:p-4">
      <div className="flex min-w-0 items-start gap-3">
        <Icon size={18} className="mt-0.5 shrink-0 text-primary" aria-hidden />
        <div className="min-w-0">
          <p className="text-label-sm font-semibold text-on-surface">{label}</p>
          <p className="mt-0.5 text-xs text-on-surface-variant">{desc}</p>
        </div>
      </div>
      <Switch checked={checked} disabled={disabled} onChange={onChange} label={label} />
    </div>
  );
}

// Pill switch styled with the design tokens: primary track when on, a sliding
// white knob. The essential row passes `disabled` to read as a locked-on switch.
function Switch({
  checked,
  disabled,
  onChange,
  label,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange?: (next: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange?.(!checked)}
      className={clsx(
        'inline-flex h-6 w-11 shrink-0 items-center rounded-full p-0.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
        checked ? 'bg-primary' : 'bg-surface-bright/40',
        disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'
      )}
    >
      <span
        className={clsx(
          'h-5 w-5 rounded-full bg-white transition-transform duration-200 ease-out',
          checked ? 'translate-x-5' : 'translate-x-0'
        )}
      />
    </button>
  );
}
